import { Injectable } from '@angular/core';
import { RemoteDataPage } from '@lycoris-nubila/ngx-lycoris';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { AddContactColumnCmd } from 'contact/command/add-contact-column.cmd';
import { RemoveContactColumnCmd } from 'contact/command/remove-contact-column.cmd';
import { ContactOutput } from 'contact/model/contact.output';
import { AddContactColumnApiMutation } from 'contact/mutation/add-contact-column.mutation';
import { DeleteContactApiMutation } from 'contact/mutation/delete-contact.mutation';
import { RemoveContactColumnApiMutation } from 'contact/mutation/remove-contact-column.mutation';
import { ContactApiPort } from 'contact/port/contact-api.port';
import { FetchContactByIdApiQuery } from 'contact/query/fetch-contact-by-id.api-query';
import { FetchContactsByIdsApiQuery } from 'contact/query/fetch-contacts-by-ids.api-query';
import { FetchContactsApiQuery } from 'contact/query/fetch-contacts.api-query';
import { FetchContactsQuery } from 'contact/query/fetch-contacts.query';
import { FetchKanbanContactsApiQuery } from 'contact/query/fetch-kanban-contacts.api-query';
import { FetchKanbanContactsQuery } from 'contact/query/fetch-kanban-contacts.query';
import { FetchProviderContactsApiQuery } from 'contact/query/fetch-provider-contacts.api-query';
import { FetchProviderContactsQuery } from 'contact/query/fetch-provider-contacts.query';
import { UserSessionPort } from 'user/port/user-session.port';

import { ApiAdapter } from '../../common/api.adpater';

@Injectable()
export class ContactApiAdapter extends ApiAdapter implements ContactApiPort {
	constructor(
		userSessionPort: UserSessionPort,
		translateService: TranslateService,
		private _fetchContactsApiQuery: FetchContactsApiQuery,
		private _deleteContactApiMutation: DeleteContactApiMutation,
		private _fetchContactByIdApiQuery: FetchContactByIdApiQuery,
		private _fetchContactsByIdsApiQuery: FetchContactsByIdsApiQuery,
		private _fetchKanbanContactsApiQuery: FetchKanbanContactsApiQuery,
		private _addContactColumnApiMutation: AddContactColumnApiMutation,
		private _fetchProviderContactsApiQuery: FetchProviderContactsApiQuery,
		private _removeContactColumnApiMutation: RemoveContactColumnApiMutation
	) {
		super(userSessionPort, translateService);
	}

	public deleteContact(id: string): Promise<string> {
		return this.mutate(this._deleteContactApiMutation, { id });
	}

	public addContactColumn(cmd: AddContactColumnCmd): Promise<ContactOutput> {
		return this.mutate(this._addContactColumnApiMutation, {
			input: { columnId: cmd.columnId, id: cmd.id, moduleId: cmd.moduleId },
		});
	}

	public removeContactColumn(
		cmd: RemoveContactColumnCmd
	): Promise<ContactOutput> {
		return this.mutate(this._removeContactColumnApiMutation, {
			input: { id: cmd.id, moduleId: cmd.moduleId },
		});
	}

	public fetchContactById(id: string): Promise<ContactOutput> {
		return this.fetch(this._fetchContactByIdApiQuery, { id });
	}

	public fetchContactsByIds(ids: string[]): Promise<ContactOutput[]> {
		return this.fetch(this._fetchContactsByIdsApiQuery, { ids });
	}

	public fetchContacts(query?: FetchContactsQuery): Observable<ContactOutput[]> {
		return this.fetch$(this._fetchContactsApiQuery, query);
	}

	public fetchKanbanContacts(
		query: FetchKanbanContactsQuery
	): Observable<ContactOutput[]> {
		return this.fetch$(this._fetchKanbanContactsApiQuery, { ...query });
	}

	public fetchProviderContacts(
		query: FetchProviderContactsQuery
	): Promise<RemoteDataPage<ContactOutput>> {
		return this.fetch(this._fetchProviderContactsApiQuery, {
			input: { ...query },
		}).then(
			(page) =>
				({
					data: page.elements,
					totalElements: page.totalElements,
					nextPageToken: page.nextPageToken,
				} as RemoteDataPage<ContactOutput>)
		);
	}
}
